import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { publicRoutes } from '../routes/public-routes-list';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService {
  private readonly inactivityTimeout = 30 * 60 * 1000; // 30 minutes of inactivity
  private readonly sessionStorageKey = 'active_browser_session';
  private readonly lastActivityStorageKey = 'last_activity_at';
  private readonly openTabsStorageKey = 'open_authenticated_tabs';
  private readonly activityEvents = [
    'click',
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'scroll',
  ];

  private timeoutId?: ReturnType<typeof setTimeout>;
  private initialized = false;
  private loggingOut = false;
  private tabRegistered = false;
  private readonly routes = publicRoutes;

  constructor(
    private authService: AuthService,
    private ngZone: NgZone,
    private router: Router
  ) { }

  start(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    if (this.hasAuthenticatedLocalSession() && !this.hasActiveBrowserSession() && this.getOpenTabsCount() === 0) {
      this.clearLocalSessionAndRedirect();
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.activityEvents.forEach((eventName: string) => {
        window.addEventListener(eventName, this.handleActivity, { passive: true });
      });

      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('beforeunload', this.handleBeforeUnload);
      window.addEventListener('storage', this.handleStorageChange);
    });

    if (this.hasAuthenticatedLocalSession()) {
      this.markBrowserSessionActive();
      this.registerCurrentTab();
      this.resetTimer();
    }
  }

  registerSuccessfulLogin(): void {
    this.loggingOut = false;
    this.markBrowserSessionActive();
    this.registerCurrentTab();
    this.recordActivity();

    if (!this.initialized) {
      this.start();
      return;
    }

    this.resetTimer();
  }

  stop(): void {
    this.clearTimer();
    this.unregisterCurrentTab();
    this.initialized = false;
    this.ngZone.runOutsideAngular(() => {
      this.activityEvents.forEach((eventName: string) => {
        window.removeEventListener(eventName, this.handleActivity);
      });

      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
      window.removeEventListener('storage', this.handleStorageChange);
    });
  }

  private readonly handleActivity = (): void => {
    if (!this.hasAuthenticatedLocalSession() || this.loggingOut) {
      return;
    }

    this.recordActivity();
    this.resetTimer();
  };

  private readonly handleVisibilityChange = (): void => {
    if (!this.hasAuthenticatedLocalSession() || document.hidden || this.loggingOut) {
      return;
    }

    if (this.isInactive()) {
      this.logoutDueToInactivity();
      return;
    }

    this.resetTimer();
  };

  private readonly handleBeforeUnload = (): void => {
    if (!this.hasAuthenticatedLocalSession()) {
      return;
    }

    // A refresh (F5/reload) and a tab/browser close both fire beforeunload, so
    // we must NOT clear the local session here — doing so logs the user out on
    // every refresh. Only release this tab's slot in the counter. The genuine
    // "browser fully closed" logout is detected on the next boot in start():
    // sessionStorage ('active_browser_session') survives a reload but dies when
    // the tab/browser is closed, so a fresh browser session with no open tabs
    // is what clears the local session — not the refresh path.
    this.unregisterCurrentTab();
  };

  private readonly handleStorageChange = (event: StorageEvent): void => {
    if (event.key !== this.lastActivityStorageKey || !this.hasAuthenticatedLocalSession() || this.loggingOut) {
      return;
    }

    if (this.isInactive()) {
      this.logoutDueToInactivity();
      return;
    }

    this.resetTimer();
  };

  private resetTimer(): void {
    this.clearTimer();

    if (!this.hasAuthenticatedLocalSession()) {
      return;
    }

    const timeoutDuration = Math.max(this.inactivityTimeout - this.getElapsedInactiveTime(), 0);

    this.ngZone.runOutsideAngular(() => {
      this.timeoutId = setTimeout(() => {
        this.logoutDueToInactivity();
      }, timeoutDuration);
    });
  }

  private logoutDueToInactivity(): void {
    if (this.loggingOut || !this.hasAuthenticatedLocalSession()) {
      return;
    }

    this.loggingOut = true;

    this.ngZone.run(() => {
      this.authService.logout().subscribe({
        next: () => this.clearLocalSessionAndRedirect(),
        error: () => this.clearLocalSessionAndRedirect(),
      });
    });
  }

  private clearLocalSessionAndRedirect(): void {
    this.clearTimer();
    this.clearLocalSessionOnly();
    this.loggingOut = false;

    if (this.router.url !== this.routes.authentication.login) {
      this.router.navigateByUrl(this.routes.authentication.login);
    }
  }

  private markBrowserSessionActive(): void {
    sessionStorage.setItem(this.sessionStorageKey, 'true');
  }

  private registerCurrentTab(): void {
    if (this.tabRegistered) {
      if (this.getOpenTabsCount() === 0) {
        localStorage.setItem(this.openTabsStorageKey, '1');
      }

      return;
    }

    this.tabRegistered = true;
    localStorage.setItem(this.openTabsStorageKey, (this.getOpenTabsCount() + 1).toString());
  }

  private unregisterCurrentTab(): void {
    if (!this.tabRegistered) {
      return;
    }

    this.tabRegistered = false;
    localStorage.setItem(this.openTabsStorageKey, Math.max(this.getOpenTabsCount() - 1, 0).toString());
  }

  private hasActiveBrowserSession(): boolean {
    return sessionStorage.getItem(this.sessionStorageKey) === 'true';
  }

  private hasAuthenticatedLocalSession(): boolean {
    return !!localStorage.getItem('access_token') && !!localStorage.getItem('user');
  }

  private recordActivity(): void {
    localStorage.setItem(this.lastActivityStorageKey, Date.now().toString());
  }

  private getOpenTabsCount(): number {
    return Number(localStorage.getItem(this.openTabsStorageKey)) || 0;
  }

  private isInactive(): boolean {
    return this.getElapsedInactiveTime() >= this.inactivityTimeout;
  }

  private getElapsedInactiveTime(): number {
    const lastActivityAt = Number(localStorage.getItem(this.lastActivityStorageKey));

    if (!lastActivityAt) {
      return 0;
    }

    return Date.now() - lastActivityAt;
  }

  private clearTimer(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  private clearLocalSessionOnly(): void {
    localStorage.clear();
    sessionStorage.removeItem(this.sessionStorageKey);
    this.tabRegistered = false;
  }
}
